import { Typography } from '@remarkable/ark-web';
import { useState } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useEnterpriseOrgSettings } from 'src/api/queries/enterpriseAdmin';
import { useEnterpriseSubscription } from 'src/api/queries/enterpriseSubscription';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { Button, Modal, Tooltip } from 'src/components';
import { enterpriseHelpers } from 'src/utils/enterpriseHelpers';
import { AbortCancelEnterpriseSubscriptionModal } from './AbortCancelEnterpriseSubscriptionModal';
import { CancelEnterpriseSubscriptionModal } from './CancelEnterpriseSubscriptionModal';
import { StartEnterpriseOffboardingModal } from './StartEnterpriseOffboardingModal';

export function CancelEnterpriseSubscription() {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isAbortCancelModalOpen, setIsAbortCancelModalOpen] = useState(false);
  const [isStartOffboardingModalOpen, setIsStartOffboardingModalOpen] =
    useState(false);
  const accessTokenClaims = useAccessTokenClaims();
  const subscription = useEnterpriseSubscription();
  const orgSettings = useEnterpriseOrgSettings();

  const canStartOffboarding = enterpriseHelpers.canStartOffboarding(
    accessTokenClaims.data,
    orgSettings.data
  );
  const hasRequestToCancel = subscription.data?.cancelRequested ?? false;

  const canRequestToCancelSubscription =
    enterpriseHelpers.canRequestToCancelSubscription(
      accessTokenClaims.data,
      subscription.data
    );

  const canAbortRequestToCancelSubscription =
    enterpriseHelpers.canAbortRequestToCancelSubscription(
      accessTokenClaims.data,
      subscription.data
    );

  const hasNoSubscription = subscription.isSuccess && !subscription.data;
  const canOnlyStartOffboarding = hasNoSubscription && canStartOffboarding;

  return (
    <div className="flex flex-col gap-24">
      {!canOnlyStartOffboarding && (
        <Typography variant="body-lg" as="h2" className="font-semibold">
          Cancel Subscription
        </Typography>
      )}

      <div className="flex flex-col justify-between gap-16 md:flex-row">
        {!canOnlyStartOffboarding && (
          <Typography variant="body-md">
            Permanently cancel your Connect for Business subscription and delete
            data.
          </Typography>
        )}

        <div className="flex flex-col gap-16 ls:flex-row">
          {canAbortRequestToCancelSubscription && (
            <Modal.Root
              open={isAbortCancelModalOpen}
              onOpenChange={setIsAbortCancelModalOpen}
            >
              <Modal.Trigger asChild>
                <Button
                  variant="secondary"
                  className="whitespace-nowrap"
                  mobileFullWidth
                  analytics={{
                    text: 'Keep subscription',
                    location: ComponentLocations.ENTERPRISE.BILLING_PAGE,
                    action: 'open abort request to cancel subscription modal',
                  }}
                >
                  Keep subscription
                </Button>
              </Modal.Trigger>

              <AbortCancelEnterpriseSubscriptionModal
                onSuccess={() => setIsAbortCancelModalOpen(false)}
              />
            </Modal.Root>
          )}
          {(hasRequestToCancel || canStartOffboarding) && (
            <Modal.Root
              open={isStartOffboardingModalOpen}
              onOpenChange={setIsStartOffboardingModalOpen}
            >
              <Tooltip
                title={
                  <Typography variant="body-md" className="max-w-320">
                    Offboarding can only be started after the subscription is
                    marked for cancellation.
                  </Typography>
                }
                enabled={!canStartOffboarding}
              >
                <Modal.Trigger asChild>
                  <Button
                    className="whitespace-nowrap"
                    mobileFullWidth
                    disabled={!canStartOffboarding}
                    variant="tertiary"
                    analytics={{
                      text: 'Start offboarding',
                      location: ComponentLocations.ENTERPRISE.BILLING_PAGE,
                      action: 'open start offboarding modal',
                    }}
                  >
                    Start Offboarding
                  </Button>
                </Modal.Trigger>
              </Tooltip>

              <StartEnterpriseOffboardingModal
                onSuccess={() => setIsStartOffboardingModalOpen(false)}
              />
            </Modal.Root>
          )}
          {canRequestToCancelSubscription && (
            <Modal.Root
              open={isCancelModalOpen}
              onOpenChange={setIsCancelModalOpen}
            >
              <Modal.Trigger asChild>
                <Button
                  variant="secondary"
                  className="whitespace-nowrap"
                  mobileFullWidth
                  analytics={{
                    text: 'Cancel subscription',
                    location: ComponentLocations.ENTERPRISE.BILLING_PAGE,
                    action: 'open request to cancel subscription modal',
                  }}
                >
                  Cancel Subscription
                </Button>
              </Modal.Trigger>

              <CancelEnterpriseSubscriptionModal
                onSuccess={() => setIsCancelModalOpen(false)}
              />
            </Modal.Root>
          )}
        </div>
      </div>
    </div>
  );
}
